/**
 * @param {string} raw
 * @returns {string}
 */
function sanitizeNumPalletsInput(raw) {
    if (raw === '') {
        return '';
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return '';
    }

    return String(Math.floor(parsed));
}

/**
 * @param {number} index zero-based pallet index
 * @returns {string} one-based display number (e.g. "1", "2")
 */
function getPalletLabel(index) {
    return String(index + 1);
}

const EMPTY_EXPIRATION_SEED = { showExpiration: false, defaultDate: '' };
const EMPTY_LOT_SEED = { defaultLot: '' };

/**
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {{ showExpiration?: boolean, defaultDate?: string }} [options.expirationSeed]
 * @param {{ defaultLot?: string }} [options.lotSeed]
 * @param {boolean} [options.isThirdPartyAsn]
 * @returns {{ id: string, companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }}
 */
function createEmptyLine({
    companySourceOptions = [],
    expirationSeed = EMPTY_EXPIRATION_SEED,
    isThirdPartyAsn = false,
    lotSeed = EMPTY_LOT_SEED,
} = {}) {
    let companySourceId = null;
    if (!isThirdPartyAsn && companySourceOptions.length === 1) {
        companySourceId = companySourceOptions[0].id;
    }

    const expiration = expirationSeed.showExpiration === true ? (expirationSeed.defaultDate ?? '') : '';
    const defaultLot = lotSeed.defaultLot ?? '';
    const supplierLot = isThirdPartyAsn && defaultLot ? defaultLot : '';

    return {
        id: crypto.randomUUID(),
        companySourceId,
        numCases: 0,
        expiration,
        supplierLot,
    };
}

/**
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {{ showExpiration?: boolean, defaultDate?: string }} [options.expirationSeed]
 * @param {{ defaultLot?: string }} [options.lotSeed]
 * @param {boolean} [options.isThirdPartyAsn]
 * @returns {{ id: string, lines: ReturnType<typeof createEmptyLine>[] }}
 */
function createEmptyPallet({
    companySourceOptions = [],
    expirationSeed = EMPTY_EXPIRATION_SEED,
    isThirdPartyAsn = false,
    lotSeed = EMPTY_LOT_SEED,
} = {}) {
    return {
        id: crypto.randomUUID(),
        lines: [createEmptyLine({ companySourceOptions, expirationSeed, isThirdPartyAsn, lotSeed })],
    };
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {number} numPallets
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {{ showExpiration?: boolean, defaultDate?: string }} [options.expirationSeed]
 * @param {{ defaultLot?: string }} [options.lotSeed]
 * @param {boolean} [options.isThirdPartyAsn]
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function syncPalletsToCount(
    pallets,
    numPallets,
    {
        companySourceOptions = [],
        expirationSeed = EMPTY_EXPIRATION_SEED,
        isThirdPartyAsn = false,
        lotSeed = EMPTY_LOT_SEED,
    } = {},
) {
    const count = Math.max(0, Math.floor(Number(numPallets) || 0));
    if (count === 0) {
        return [];
    }

    const next = pallets.slice(0, count);
    while (next.length < count) {
        next.push(createEmptyPallet({ companySourceOptions, expirationSeed, isThirdPartyAsn, lotSeed }));
    }
    return next;
}

/**
 * Fills unset companySourceId on existing lines when exactly one source option is available.
 *
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {boolean} [options.isThirdPartyAsn]
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function autoFillSingleCompanySourceOnPallets(pallets, { companySourceOptions = [], isThirdPartyAsn = false } = {}) {
    if (isThirdPartyAsn || companySourceOptions.length !== 1 || pallets.length === 0) {
        return pallets;
    }

    const sourceId = companySourceOptions[0].id;
    let didUpdate = false;

    const next = pallets.map((pallet) => ({
        ...pallet,
        lines: pallet.lines.map((line) => {
            if (line.companySourceId != null) {
                return line;
            }
            didUpdate = true;
            return { ...line, companySourceId: sourceId };
        }),
    }));

    return didUpdate ? next : pallets;
}

/**
 * @param {string | number | undefined | null} raw
 * @returns {number | undefined}
 */
function parseNonNegativeNumCases(raw) {
    if (raw === '' || raw === undefined || raw === null) {
        return undefined;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return undefined;
    }

    return parsed;
}

/**
 * @typedef {Object} QuickFillValues
 * @property {number} [companySourceId]
 * @property {number} [numCases]
 * @property {string} [expiration]
 * @property {string} [supplierLot]
 */

/**
 * @param {ReturnType<typeof createEmptyLine>[]} lines
 * @param {QuickFillValues} values
 * @param {boolean} isThirdPartyAsn
 * @returns {ReturnType<typeof createEmptyLine>[]}
 */
function applyQuickFillToLines(lines, values, isThirdPartyAsn) {
    return lines.map((line) => {
        const next = { ...line };

        if (!isThirdPartyAsn && values.companySourceId !== undefined) {
            next.companySourceId = values.companySourceId;
        }
        if (values.numCases !== undefined) {
            const numCases = parseNonNegativeNumCases(values.numCases);
            if (numCases !== undefined) {
                next.numCases = numCases;
            }
        }
        if (values.expiration !== undefined) {
            next.expiration = values.expiration;
        }
        if (values.supplierLot !== undefined) {
            next.supplierLot = values.supplierLot;
        }

        return next;
    });
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {QuickFillValues} values
 * @param {boolean} isThirdPartyAsn
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function applyQuickFillToPallets(pallets, values, isThirdPartyAsn) {
    return pallets.map((pallet) => ({
        ...pallet,
        lines: applyQuickFillToLines(pallet.lines, values, isThirdPartyAsn),
    }));
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {string} palletId
 * @param {QuickFillValues} values
 * @param {boolean} isThirdPartyAsn
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function applyQuickFillToPallet(pallets, palletId, values, isThirdPartyAsn) {
    return pallets.map((pallet) =>
        pallet.id === palletId
            ? {
                  ...pallet,
                  lines: applyQuickFillToLines(pallet.lines, values, isThirdPartyAsn),
              }
            : pallet,
    );
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {{ type: string, [key: string]: unknown }} action
 * @param {{ showExpiration?: boolean, defaultDate?: string }} [expirationSeed]
 * @param {{ defaultLot?: string }} [lotSeed]
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function palletStagingReducer(pallets, action, expirationSeed = EMPTY_EXPIRATION_SEED, lotSeed = EMPTY_LOT_SEED) {
    switch (action.type) {
        case 'SYNC_NUM_PALLETS':
            return syncPalletsToCount(pallets, Number(action.numPallets) || 0, {
                companySourceOptions: /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                expirationSeed,
                isThirdPartyAsn: Boolean(action.isThirdPartyAsn),
                lotSeed,
            });
        case 'AUTO_FILL_SINGLE_SOURCE':
            return autoFillSingleCompanySourceOnPallets(pallets, {
                companySourceOptions: /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                isThirdPartyAsn: Boolean(action.isThirdPartyAsn),
            });
        case 'ADD_LINE':
            return pallets.map((pallet) =>
                pallet.id === action.palletId
                    ? {
                          ...pallet,
                          lines: [
                              ...pallet.lines,
                              createEmptyLine({
                                  companySourceOptions:
                                      /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                                  expirationSeed,
                                  isThirdPartyAsn: Boolean(action.isThirdPartyAsn),
                                  lotSeed,
                              }),
                          ],
                      }
                    : pallet,
            );
        case 'REMOVE_LINE':
            return pallets.map((pallet) => {
                if (pallet.id !== action.palletId || pallet.lines.length <= 1) {
                    return pallet;
                }
                return {
                    ...pallet,
                    lines: pallet.lines.filter((line) => line.id !== action.lineId),
                };
            });
        case 'UPDATE_LINE':
            return pallets.map((pallet) =>
                pallet.id === action.palletId
                    ? {
                          ...pallet,
                          lines: pallet.lines.map((line) =>
                              line.id === action.lineId ? { ...line, .../** @type {object} */ (action.patch) } : line,
                          ),
                      }
                    : pallet,
            );
        case 'APPLY_QUICK_FILL':
            return applyQuickFillToPallets(
                pallets,
                /** @type {QuickFillValues} */ (action.values),
                Boolean(action.isThirdPartyAsn),
            );
        case 'APPLY_QUICK_FILL_TO_PALLET':
            return applyQuickFillToPallet(
                pallets,
                /** @type {string} */ (action.palletId),
                /** @type {QuickFillValues} */ (action.values),
                Boolean(action.isThirdPartyAsn),
            );
        default:
            return pallets;
    }
}

export {
    applyQuickFillToLines,
    applyQuickFillToPallet,
    applyQuickFillToPallets,
    autoFillSingleCompanySourceOnPallets,
    createEmptyLine,
    createEmptyPallet,
    EMPTY_EXPIRATION_SEED,
    EMPTY_LOT_SEED,
    getPalletLabel,
    palletStagingReducer,
    parseNonNegativeNumCases,
    sanitizeNumPalletsInput,
    syncPalletsToCount,
};
